import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'lib/auth';

import {
  type GetStartedProgress,
  getStartedProgressKey,
  isGetStartedEligible,
} from '../get-started-progress';

const PROGRESS_STALE_TIME_MS = 5 * 60_000;

interface UseGetStartedProgressOptions {
  /**
   * `fresh` refetches on every mount and focus for the surfaces where a step
   * may just have been completed; `cached` serves the sidebar from cache.
   */
  freshness?: 'cached' | 'fresh';
}

export const useGetStartedProgress = ({
  freshness = 'cached',
}: UseGetStartedProgressOptions = {}) => {
  const auth = useAuth();
  const user = auth.user?.user;
  const teamId = user?.team?.id;
  const isEligible = isGetStartedEligible(user);
  const isFresh = freshness === 'fresh';
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: getStartedProgressKey(teamId),
    enabled: isEligible && !!teamId,
    queryFn: async ({ signal }): Promise<GetStartedProgress> => {
      const response = await auth.authFetch(
        `/org/v1/teams/${teamId}/get-started/`,
        {
          signal,
        },
      );
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    },
    staleTime: PROGRESS_STALE_TIME_MS,
    refetchOnMount: isFresh ? 'always' : true,
    refetchOnWindowFocus: isFresh ? 'always' : true,
    retry: false,
  });
  return {
    data,
    isPending,
    isError,
    refetch,
    isEligible,
    isVisible: isEligible && !data?.is_hidden,
  };
};
